import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Flame, Send, ListChecks, Check, Cake, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Onboarding() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [roles, setRoles] = useState(["submit", "roast"]);
  const [birthdate, setBirthdate] = useState("");
  const [cg, setCg] = useState(false);
  const [saving, setSaving] = useState(false);
  const [blocked, setBlocked] = useState(false);

  const toggleRole = (r) =>
    setRoles((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));

  const ageFrom = (d) => {
    if (!d) return null;
    const b = new Date(d), t = new Date();
    let a = t.getFullYear() - b.getFullYear();
    const m = t.getMonth() - b.getMonth();
    if (m < 0 || (m === 0 && t.getDate() < b.getDate())) a--;
    return a;
  };
  const age = ageFrom(birthdate);

  const submit = async () => {
    if (!birthdate) return toast.error("Please enter your date of birth.");
    if (age !== null && age < 18) { setBlocked(true); return; }
    if (!cg) return toast.error("Please accept the content guidelines to continue.");
    if (roles.length === 0) return toast.error("Pick at least one role.");
    setSaving(true);
    try {
      const res = await api.post("/onboarding", { roles, birthdate, consent_guidelines: cg });
      setUser(res.data);
      navigate("/app/submit");
    } catch (e) {
      if (e.response?.status === 403) setBlocked(true);
      else toast.error(e.response?.data?.detail || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const roleCards = [
    { id: "submit", label: "Submit", icon: Send, desc: "Pay to get roasted." },
    { id: "roast", label: "Roast", icon: ListChecks, desc: "Write roasts, get paid." },
  ];

  const todayStr = new Date().toISOString().slice(0, 10);

  if (blocked) {
    return (
      <div className="min-h-screen grain flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md text-center animate-fade-up" data-testid="age-blocked">
          <div className="mx-auto w-14 h-14 rounded-full bg-destructive/15 flex items-center justify-center mb-5">
            <AlertTriangle className="text-destructive" size={26} />
          </div>
          <h1 className="font-heading font-bold uppercase tracking-tighter text-3xl">You must be 18+</h1>
          <p className="text-zinc-400 mt-3">RoastLit is strictly for adults. Based on the date of birth you entered, you're not old enough to use it yet. Come back when you're 18.</p>
          <button data-testid="age-blocked-logout" onClick={async () => { try { await api.post("/auth/logout"); } catch (_) {} localStorage.removeItem("rb_token"); window.location.href = "/"; }}
            className="mt-6 px-6 py-3 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 font-bold transition-colors">
            Back to home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grain flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg animate-fade-up">
        <div className="flex items-center gap-2 mb-2"><Flame className="text-brand" size={22} /><span className="font-heading font-bold text-lg tracking-tighter">RoastLit</span></div>
        <h1 className="font-heading font-bold uppercase tracking-tighter text-3xl sm:text-4xl">Welcome, {user?.name?.split(" ")[0]}</h1>
        <p className="text-zinc-400 mt-2 mb-8">Set up your account. Most people do both.</p>

        <div className="grid grid-cols-2 gap-3 mb-8">
          {roleCards.map((r) => {
            const active = roles.includes(r.id);
            return (
              <button key={r.id} data-testid={`role-${r.id}`} onClick={() => toggleRole(r.id)}
                className={`text-left rounded-sm border p-4 transition-all ${active ? "border-brand bg-brand/10" : "border-white/10 bg-zinc-900/60 hover:border-white/25"}`}>
                <div className="flex items-center justify-between mb-2">
                  <r.icon size={20} className={active ? "text-brand" : "text-zinc-400"} />
                  {active && <Check size={16} className="text-brand" />}
                </div>
                <div className="font-bold">{r.label}</div>
                <div className="text-xs text-zinc-500">{r.desc}</div>
              </button>
            );
          })}
        </div>

        <div className="mb-8">
          <label className="text-xs tracking-[0.2em] uppercase text-zinc-400 font-bold flex items-center gap-2 mb-2">
            <Cake size={14} className="text-brand" /> Your date of birth
          </label>
          <input data-testid="birthdate-input" type="date" max={todayStr} value={birthdate} onChange={(e) => setBirthdate(e.target.value)}
            className="w-full bg-zinc-950 border border-zinc-800 rounded-sm p-3 text-sm text-zinc-100 focus:border-brand focus:outline-none [color-scheme:dark]" />
          <p className="text-[11px] text-zinc-600 mt-2">RoastLit is 18+ only. We ask for your real birthdate — we do not accept a checkbox.{age !== null && age >= 0 && age < 130 && <span className="text-zinc-400"> You're {age}.</span>}</p>
        </div>

        <div className="space-y-3 mb-8">
          <label className="flex items-start gap-3 cursor-pointer">
            <input data-testid="consent-guidelines" type="checkbox" checked={cg} onChange={(e) => setCg(e.target.checked)} className="mt-1 accent-[#ff4500] w-4 h-4" />
            <span className="text-sm text-zinc-300">
              I have read the <strong>content guidelines</strong>: roasts target appearance, career, taste and skill — never race, religion, disability, gender identity, orientation, or real-world tragedy.
            </span>
          </label>
        </div>

        <button data-testid="onboarding-submit" onClick={submit} disabled={saving}
          className="w-full py-3.5 rounded-full bg-brand hover:bg-brand-hover text-black font-bold transition-colors disabled:opacity-50">
          {saving ? "Saving…" : "Enter RoastLit"}
        </button>
      </div>
    </div>
  );
}
